import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Terminal as XTerm } from '@xterm/xterm'
import { FitAddon } from '@xterm/addon-fit'
import { WebglAddon } from '@xterm/addon-webgl'
import { WebLinksAddon } from '@xterm/addon-web-links'
import type { TermClientMessage, TermServerMessage } from '@one-deployment/protocol'
import type { AgentSession } from '@one-deployment/api'
import { EmptyState } from '@/components/status'
import { useMe } from '@/features/auth/useMe'
import { openBoxSocket, ReconnectingBoxSocket, type BoxSocketState } from '@/lib/box'
import { terminalMode } from '@/lib/can'
import { DRAG_PATH, mention, uploadDropped, useTermInsert } from '@/lib/files'
import { ResizeHandle } from '@/components/ResizeHandle'
import { FolderTree } from 'lucide-react'
import { Button } from '@/components/shadcn/button'
import { FilePanel } from './FilePanel'
import { FleetNotice } from '@/features/servers/FleetNotice'

/**
 * A session's tmux pane, straight from the box (03 §5.5): fully interactive
 * for a dev, read-only for a viewer. PTY bytes are binary frames; control
 * messages are JSON text. Read-only is enforced by the box token, not here.
 */
const FILES_KEY = 'trisof.files.panel'
const FILES_WIDTH_KEY = 'trisof.files.width'

export type TermStatus = { state: BoxSocketState; viewers: number; readOnly: boolean }

/** `bare` drops the status row; the caller shows `onStatus` in its own header. */
/** `shell` is a bash in the session's worktree (ONE-11) instead of Claude's pane. */
export function TerminalPane({ session, project, run = false, shell = false, actions, bare = false, onStatus }: { session: AgentSession | undefined; project: string; run?: boolean; shell?: boolean; actions?: ReactNode; bare?: boolean; onStatus?: (s: TermStatus) => void }) {
  const host = useRef<HTMLDivElement>(null)
  const me = useMe().data
  const mode = terminalMode(me, project)
  const [state, setState] = useState<BoxSocketState>('connecting')
  const [viewers, setViewers] = useState(0)
  // Typing takes the session from the robot (features/12); it waits until
  // you are quiet for the project's window, or you hand it back here.
  const [typed, setTyped] = useState(false)
  const resumeRef = useRef<() => void>(() => {})
  // Types text from the file panel (an `@path`) as if the person typed it.
  const typeRef = useRef<(text: string) => void>(() => {})
  // The file panel sits beside Claude's terminal; the dev server's has none.
  const [files, setFiles] = useState(() => localStorage.getItem(FILES_KEY) !== 'hidden')
  const [filesWidth, setFilesWidth] = useState(() => Number(localStorage.getItem(FILES_WIDTH_KEY)) || 240)
  const row = useRef<HTMLDivElement>(null)
  // A file dropped from the computer: uploaded into uploads/, then referenced.
  const [drop, setDrop] = useState<{ over: boolean; text: string | null; error: boolean }>({ over: false, text: null, error: false })
  const dropFiles = (list: FileList) => {
    if (!session || mode !== 'rw') return
    const files = [...list]
    setDrop({ over: false, text: 'Uploading…', error: false })
    uploadDropped(session.id, files, (text) => setDrop({ over: false, text, error: false })).then(
      (paths) => { setDrop({ over: false, text: null, error: false }); typeRef.current(paths.map(mention).join('')) },
      (e: Error) => { setDrop({ over: false, text: e.message, error: true }); setTimeout(() => setDrop((d) => (d.error ? { ...d, text: null } : d)), 6000) },
    )
  }
  const showFiles = (on: boolean) => { setFiles(on); localStorage.setItem(FILES_KEY, on ? 'shown' : 'hidden') }

  useEffect(() => {
    if (!host.current || !session) return
    setState('connecting')
    const term = new XTerm({
      fontFamily: '"JetBrains Mono Variable", "JetBrains Mono", monospace',
      fontSize: 13,
      cursorBlink: true,
      theme: { background: '#0b0b0c', foreground: '#e6e6e6', cursor: '#8a8f98', selectionBackground: '#5e6ad244' },
      scrollback: 10_000,
      // tmux has the mouse (wheel scrolls history); Option-drag still selects.
      macOptionClickForcesSelection: true,
    })
    const fit = new FitAddon()
    term.loadAddon(fit)
    term.loadAddon(new WebLinksAddon())
    term.open(host.current)
    // The DOM renderer draws box characters from the font, which leaves gaps
    // between rows in Claude's tables; WebGL draws them as joined lines.
    try {
      const gl = new WebglAddon()
      gl.onContextLoss(() => gl.dispose())
      term.loadAddon(gl)
    } catch { /* no WebGL: the DOM renderer still works */ }
    fit.fit()
    term.focus()

    const enc = new TextEncoder()
    const control = (m: TermClientMessage) => sock.send(JSON.stringify(m))
    const sock = new ReconnectingBoxSocket(() => openBoxSocket({ sessionId: session.id, run, ...(shell ? { shell } : {}) }, 'terminal', mode), {
      onOpen: () => { fit.fit(); control({ type: 'resize', cols: term.cols, rows: term.rows }) },
      onMessage: (data) => {
        if (typeof data !== 'string') { term.write(new Uint8Array(data)); return }
        const m = JSON.parse(data) as TermServerMessage
        if (m.type === 'viewers') setViewers(m.count)
        else if (m.type === 'exit') setState('ended')
      },
      onState: (s) => setState(s),
      isFinal: (code) => code === 4000,
    })
    sock.start()
    const input = term.onData((d) => { if (mode === 'rw') { sock.send(enc.encode(d)); setTyped(true) } })
    resumeRef.current = () => { control({ type: 'resume' }); setTyped(false) }
    typeRef.current = (text) => { if (mode === 'rw') { sock.send(enc.encode(text)); setTyped(true); term.focus() } }
    // xterm passes Cmd combos to the browser and sends nothing for most of
    // them, so the Mac editing keys people expect from Terminal/iTerm do
    // nothing. Map them to the readline bytes those apps send.
    term.attachCustomKeyEventHandler((e) => {
      if (e.type !== 'keydown') return true
      // Shift/Option+Enter is Claude's newline; bash wants a plain Enter.
      const seq = shell && e.key === 'Enter' ? undefined : macKey(e, term)
      if (seq === undefined) return true
      e.preventDefault()
      if (seq && mode === 'rw') { sock.send(enc.encode(seq)); setTyped(true) }
      return false
    })
    // One fit per frame, and tmux told only when the grid really changed:
    // a panel opening fires many observations while it lays out.
    let frame = 0
    let sent = `${term.cols}x${term.rows}`
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        fit.fit()
        const now = `${term.cols}x${term.rows}`
        if (now !== sent) { sent = now; control({ type: 'resize', cols: term.cols, rows: term.rows }) }
      })
    })
    ro.observe(host.current)
    const ping = setInterval(() => control({ type: 'ping' }), 25_000)
    return () => {
      clearInterval(ping)
      cancelAnimationFrame(frame)
      ro.disconnect()
      input.dispose()
      sock.stop()
      term.dispose()
    }
  }, [session?.id, mode, run, shell])

  useEffect(() => { onStatus?.({ state, viewers, readOnly: mode === 'ro' }) }, [state, viewers, mode, onStatus])

  const pending = useTermInsert((s) => s.next)
  useEffect(() => { if (pending && !run && pending.sessionId === session?.id) typeRef.current(pending.text) }, [pending, run, session?.id])

  if (!session) return <EmptyState title="No session open" body="Pick a session, or start a new one." />

  return (
    <div className="flex h-full min-h-0 flex-col">
      {!bare && <div className="flex h-7 shrink-0 items-center gap-3 border-b border-border px-3 text-xs text-fg-subtle">
        <span title={state} className={state === 'open' ? 'text-ok' : state === 'unreachable' || state === 'ended' ? 'text-err' : 'text-warn'}>●{state !== 'open' && state !== 'connecting' && state !== 'reconnecting' && ` ${state}`}</span>
        <span>{viewers} watching</span>
        {mode === 'ro' && <span>read-only</span>}
        <span className="ml-auto font-mono text-fg-faint">{run ? `${session.tmuxSession}-run` : shell ? `${session.tmuxSession}-sh` : session.tmuxSession}</span>
        {actions}
      </div>}
      <FleetNotice unreachable={state === 'unreachable'} />
      {/* Only a ticket's robot session waits on a human; a plain session has no one to hand back to. */}
      {typed && !run && !shell && session.jobId && state === 'open' && <div className="flex items-center gap-3 border-b border-warn/30 bg-warn/10 px-3 py-1.5 text-xs">
        <span>You have the session. Claude waits, and carries on after 10 quiet minutes.</span>
        <Button type="button" variant="outline" size="xs" className="ml-auto h-5 rounded-sm" onClick={() => resumeRef.current()}>Resume now</Button>
      </div>}
      {state === 'ended' && <div className="border-b border-border bg-surface-2 px-3 py-1.5 text-xs text-fg-muted">This session has ended.</div>}
      <div ref={row} className="flex min-h-0 flex-1">
        {!run && !bare && files && <>
          <div style={{ width: filesWidth }} className="h-full min-h-0 shrink-0 overflow-hidden"><FilePanel sessionId={session.id} mode={mode} onHide={() => showFiles(false)} /></div>
          <ResizeHandle label="Resize the file panel" onResize={(x) => {
            const w = Math.min(600, Math.max(160, x - (row.current?.getBoundingClientRect().left ?? 0)))
            setFilesWidth(w); localStorage.setItem(FILES_WIDTH_KEY, String(w))
          }} />
        </>}
        {!run && !bare && !files && <Button type="button" variant="ghost" title="Show files" aria-label="Show files" onClick={() => showFiles(true)}
          className="h-auto shrink-0 items-start rounded-none border-r border-border px-1 pt-1.5 text-fg-faint hover:bg-hover"><FolderTree size={13} /></Button>}
        {/* A file dragged from the panel lands as `@path`, the way Claude Code references files. */}
        <div className="relative h-full min-h-0 min-w-0 flex-1 overflow-hidden bg-bg"
          onDragOver={(e) => {
            if (run) return
            const t = e.dataTransfer.types
            if (t.includes(DRAG_PATH) || (t.includes('Files') && mode === 'rw')) { e.preventDefault(); if (t.includes('Files') && !drop.over) setDrop((d) => ({ ...d, over: true })) }
          }}
          onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDrop((d) => ({ ...d, over: false })) }}
          onDrop={(e) => {
            if (run) return
            const p = e.dataTransfer.getData(DRAG_PATH)
            if (p) { e.preventDefault(); typeRef.current(mention(p)); return }
            if (e.dataTransfer.files.length) { e.preventDefault(); dropFiles(e.dataTransfer.files) }
          }}>
          {/* Padding sits outside the element xterm fits into: FitAddon counts the
              parent's padding as room, which made the grid a row too tall and
              hid Claude's input line at the bottom. */}
          <div className="absolute inset-1"><div ref={host} className="h-full w-full" /></div>
          {drop.over && <div className="pointer-events-none absolute inset-1 flex items-center justify-center rounded border-2 border-dashed border-accent bg-accent/10 text-sm text-fg">Drop to upload into uploads/ and reference it</div>}
          {drop.text && <div role={drop.error ? 'alert' : 'status'} className={`absolute bottom-2 right-2 rounded border border-border bg-surface-2 px-2 py-1 text-xs ${drop.error ? 'text-err' : 'text-fg-muted'}`}>{drop.text}</div>}
        </div>
      </div>
    </div>
  )
}

/**
 * Bytes for a Mac editing shortcut, '' to swallow it, or undefined to let
 * xterm/the browser handle it (Cmd+C/V copy and paste, Cmd+R, tabs…).
 */
export function macKey(e: KeyboardEvent, term: { clear(): void; hasSelection(): boolean }): string | undefined {
  const { key, metaKey: cmd, altKey: opt, shiftKey: shift, ctrlKey: ctrl } = e
  if (ctrl) return undefined
  if (cmd && !opt) {
    if (key === 'Backspace') return '\x15' // kill to line start
    if (key === 'Delete') return '\x0b' // kill to line end
    if (key === 'ArrowLeft') return '\x01'
    if (key === 'ArrowRight') return '\x05'
    if (key === 'k' || key === 'K') { term.clear(); return '' }
    return undefined
  }
  if (opt && !cmd) {
    if (key === 'Backspace') return '\x17' // previous word
    if (key === 'Delete') return '\x1bd'
    if (key === 'ArrowLeft') return '\x1bb'
    if (key === 'ArrowRight') return '\x1bf'
    // Option+Enter is a newline in Claude, same as Shift+Enter.
    if (key === 'Enter') return '\x1b\r'
    return undefined
  }
  // Claude reads ESC+CR as "new line, don't send".
  if (shift && key === 'Enter') return '\x1b\r'
  return undefined
}
